import * as RadixTabs from '@radix-ui/react-tabs';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router';
import { cn } from '../lib/cn';

/**
 * Tabs, styled once.
 *
 * Radix gives the keyboard model (arrow keys move between tabs, Home/End jump,
 * the panel is labelled by its tab) that hand-rolled tab buttons on several
 * screens did not have. Pair with `useTabParam` so the open tab is in the URL
 * — a link to "the Groups tab of People" should open the Groups tab.
 */

export const Tabs = RadixTabs.Root;

export function TabsList({ className, ...props }: RadixTabs.TabsListProps) {
  return (
    <RadixTabs.List
      className={cn('flex items-center gap-1 overflow-x-auto border-b border-line', className)}
      {...props}
    />
  );
}

export function TabsTrigger({ className, ...props }: RadixTabs.TabsTriggerProps) {
  return (
    <RadixTabs.Trigger
      className={cn(
        '-mb-px inline-flex shrink-0 items-center gap-1.5 border-b-2 border-transparent px-2.5 py-2',
        'text-body text-ink-muted transition-colors hover:text-ink',
        'data-[state=active]:border-accent data-[state=active]:font-medium data-[state=active]:text-ink',
        className,
      )}
      {...props}
    />
  );
}

export function TabsContent({ className, ...props }: RadixTabs.TabsContentProps) {
  return <RadixTabs.Content className={cn('outline-none', className)} {...props} />;
}

/**
 * The selected tab, kept in the query string.
 *
 * Unknown values fall back to the first allowed tab, so a stale or hand-edited
 * link opens something sensible rather than an empty panel.
 */
export function useTabParam<T extends string>(
  key: string,
  allowed: readonly T[],
): [T, (next: string) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get(key);
  const current = (allowed as readonly string[]).includes(raw ?? '') ? (raw as T) : allowed[0]!;

  const set = useCallback(
    (next: string) => {
      setParams(
        (previous) => {
          const updated = new URLSearchParams(previous);
          if (next === allowed[0]) updated.delete(key);
          else updated.set(key, next);
          return updated;
        },
        { replace: true },
      );
    },
    [allowed, key, setParams],
  );

  return [current, set];
}
